import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import _ from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import grey from '@material-ui/core/colors/grey';
import red from '@material-ui/core/colors/red';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import SentIcon from '../../../../../icons/SentIcon';
import TrashIcon from '../../../../../icons/TrashIcon';
import TypographyTitle from '../../../../../TypographyTitle';
import Button from '../../../../../Button';
import TypographyParagraph from '../../../../../TypographyParagraph';
import ShareAction from '../../../../../enums/ShareAction';

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    width: 506,
  },
  titleContainer: props => ({
    padding: theme.spacing(4, 1, 1.5, 3),
    display: 'flex',
    color: props.type === ShareAction.RESEND ? theme.palette.primary.main : red[400],
  }),
  titleText: {
    margin: 0,
    fontSize: 28,
    color: 'inherit',
  },
  dialogContent: {
    width: '100%',
    paddingLeft: 44,
  },
  bodyText: {
    fontSize: 16,
    color: grey[700],
  },
  actions: {
    padding: '19px 25px 25px 0',
  },
  resendIcon: {
    fontSize: 33,
    margin: '-2px 8px 0px 0px',
  },
  removeIcon: {
    fontSize: 24,
    margin: '1px 12px 0px 6px',
  },
  buttonLabel: {
    fontSize: 13,
  },
  cancelButton: {
    borderColor: grey[400],
    color: grey[500],
    marginRight: theme.spacing(0.5),
    '&:hover': {
      borderColor: grey[600],
      backgroundColor: grey[200],
    }
  },
  redConfirmButton: {
    color: '#ffffff',
    backgroundColor: red[400],
    '&:hover': {
      backgroundColor: red[700],
    }
  },
}))

function ShareReportActionsDialog({
  type,
  emails,
  sharedAndInherited,
  handleConfirm,
  handleCancel,
  successCallback,
}) {
  const classes = useStyles({ type });
  const intersection = _.intersection(emails, sharedAndInherited);

  const resendContent = emails.length === 1 ? (
    <React.Fragment>
      <strong>{emails[0]}</strong> already has access to this report. Do you still want to share this report directly?
    </React.Fragment>
  ) : (
    <React.Fragment>
      This will resend the report email to the following addresses:
      <br/>
      <br/>
      {emails.map((str, i) => <strong key={`${str}-${i}`}>{str}<br/></strong>)}
    </React.Fragment>
  );

  const removeAccessContent = (
    <React.Fragment>
      This will remove access to the following addresses:
      <br/>
      <br/>
      {emails.map((str, i) => <strong key={`${str}-${i}`}>{str}<br/></strong>)}
      {intersection.length > 0 && (
        <React.Fragment>
          <br/>
          The following will still have inherited access:
          <br/>
          <br/>
          {intersection.map((str, i) => <strong key={`${str}-${i}`}>{str}<br/></strong>)}
        </React.Fragment>
      )}
    </React.Fragment>
  );

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      open={!!type}
      maxWidth='sm'
      classes={{ paper: classes.dialogPaper }}
    >
      <div className={classes.titleContainer}>
        {type === ShareAction.RESEND && <SentIcon className={classes.resendIcon} />}
        {type === ShareAction.REMOVE && <TrashIcon className={classes.removeIcon} />}
        <TypographyTitle className={classes.titleText}>
          {type === ShareAction.RESEND && 'Resend report email?'}
          {type === ShareAction.REMOVE && 'Remove access?'}
        </TypographyTitle>
      </div>
      <DialogContent>
        <div className={classes.dialogContent}>
          <TypographyParagraph className={classes.bodyText}>
            {type === ShareAction.RESEND && resendContent}
            {type === ShareAction.REMOVE && removeAccessContent}
          </TypographyParagraph>
        </div>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button 
          className={classes.cancelButton} 
          onClick={handleCancel} 
          variant="outlined" 
          color="inherit"
          classes={{ label: classes.buttonLabel }}
        >
          Cancel
        </Button>
        <Button 
          className={clsx({ [classes.redConfirmButton]: type === ShareAction.REMOVE })}
          onClick={e => handleConfirm(e, emails, successCallback)} 
          variant="contained"
          classes={{ label: classes.buttonLabel }}
        >
          {type === ShareAction.RESEND && 'Resend'}
          {type === ShareAction.REMOVE && 'Remove'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ShareReportActionsDialog.propTypes = {
  type: PropTypes.string.isRequired,
  emails: PropTypes.array.isRequired,
  sharedAndInherited: PropTypes.array.isRequired,
  handleConfirm: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
  successCallback: PropTypes.func,
}

export default ShareReportActionsDialog;



// WEBPACK FOOTER //
// ./src/components/SRHHeaders/SummaryReportHeader/components/ShareReport/components/ShareReportActionsDialog.jsx